import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  ApiEnvelope,
  FamiliesPage,
  FamilySort,
  IssueDetail,
  IssuesOverview,
  ListingDetail,
  ListingFacets,
  ListingFilter,
  ListingSort,
  ListingsPage,
} from '@seller-portal/types';
import { api } from '@/services/api';

/** The seller's catalog, read from the Merx API's seller-scoped routes. */

const PAGE_SIZE = 50;

export async function fetchListings(
  sellerId: string,
  body: { filter?: ListingFilter; sort?: ListingSort; pageSize?: number; cursor?: string }
): Promise<ListingsPage> {
  const res = await api.post<ApiEnvelope<ListingsPage>>(
    `/sellers/${sellerId}/catalog/listings`,
    body
  );
  return res.data.data;
}

export function useListings(
  sellerId: string,
  filter: ListingFilter,
  sort: ListingSort | undefined,
  enabled = true
) {
  return useInfiniteQuery({
    enabled,
    queryKey: ['seller', sellerId, 'listings', filter, sort ?? null],
    queryFn: ({ pageParam }) =>
      fetchListings(sellerId, {
        filter,
        ...(sort ? { sort } : {}),
        pageSize: PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

/** `countsOnly`: totals and plain buckets without the category attributes, a much cheaper read. */
export async function fetchFacets(
  sellerId: string,
  filter: ListingFilter,
  countsOnly = false
): Promise<ListingFacets> {
  return (
    await api.post<ApiEnvelope<ListingFacets>>(`/sellers/${sellerId}/catalog/facets`, {
      filter,
      ...(countsOnly ? { attributes: false } : {}),
    })
  ).data.data;
}

export function facetsKey(sellerId: string, filter: ListingFilter, countsOnly = false) {
  return ['seller', sellerId, countsOnly ? 'counts' : 'facets', filter] as const;
}

export function useFacets(sellerId: string, filter: ListingFilter, enabled = true) {
  return useQuery({
    enabled,
    queryKey: facetsKey(sellerId, filter),
    queryFn: () => fetchFacets(sellerId, filter),
    placeholderData: keepPreviousData,
  });
}

export function useListing(sellerId: string, id: string) {
  return useQuery({
    queryKey: ['seller', sellerId, 'listing', id],
    queryFn: async () =>
      (
        await api.get<ApiEnvelope<ListingDetail>>(
          `/sellers/${sellerId}/catalog/listings/${encodeURIComponent(id)}`
        )
      ).data.data,
  });
}

const FAMILY_PAGE = 50;

export function useFamilies(
  sellerId: string,
  filter: ListingFilter,
  sort: FamilySort | undefined,
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: ['seller', sellerId, 'families', filter, sort ?? null],
    queryFn: async ({ pageParam }) =>
      (
        await api.post<ApiEnvelope<FamiliesPage>>(`/sellers/${sellerId}/catalog/families`, {
          filter,
          ...(sort ? { sort } : {}),
          pageSize: FAMILY_PAGE,
          ...(pageParam ? { cursor: pageParam } : {}),
        })
      ).data.data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useIssues(sellerId: string) {
  return useQuery({
    queryKey: ['seller', sellerId, 'issues'],
    queryFn: async () =>
      (await api.get<ApiEnvelope<IssuesOverview>>(`/sellers/${sellerId}/catalog/issues`)).data.data,
  });
}

export async function fetchIssuePage(
  sellerId: string,
  type: string,
  cursor?: string,
  pageSize = 50
): Promise<IssueDetail> {
  const res = await api.get<ApiEnvelope<IssueDetail>>(
    `/sellers/${sellerId}/catalog/issues/${encodeURIComponent(type)}`,
    {
      params: { pageSize, ...(cursor ? { cursor } : {}) },
    }
  );
  return res.data.data;
}

export function useIssue(sellerId: string, type: string) {
  return useInfiniteQuery({
    queryKey: ['seller', sellerId, 'issue', type],
    queryFn: ({ pageParam }) => fetchIssuePage(sellerId, type, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
